/**
 * Show-day time logic: is an event today, has it started, is it over — in the VENUE's own
 * time zone. Pure, no imports: the site (src/lib/today.ts), the edge and the tests
 * (scripts/showtime.test.mjs) all run exactly this.
 *
 * WHY: 54% of human clicks (Oct 15-18) were for a show today or tomorrow, and the site used to
 * judge "today" by the visitor's clock or the UTC date: a 6 PM Las Vegas show was "past" at
 * 5 PM PT, and nothing said a show had started.
 *
 * THE ZONE, best source first:
 *   1. place.tz — the venue's IANA zone (SeatGeek rows). Legacy names such as 'US/Pacific'
 *      are fine; an unknown name is skipped, never thrown.
 *   2. place.offset — the event's fixed UTC offset at its start (HelloTickets rows).
 *   3. (country, region) — the region's majority zone (TicketNetwork rows carry only a state).
 *      A guess can be an hour off (East Tennessee is Eastern), so 'started' waits an extra hour.
 *   4. nothing: no time-based states at all, and calendar days by the visitor's own date.
 * A whole country is never assumed to be on one zone unless it really is.
 */

/** @typedef {{ tz?: string | null, offset?: string | null, region?: string | null }} Place */
/** @typedef {{ date: string, time?: string | null, country?: string | null, place?: Place | null, category?: string | null, kind?: string | null, genre?: string | null, name?: string | null }} ShowEvent */
/** @typedef {{ kind: 'tz' | 'region', zone: string } | { kind: 'offset', minutes: number }} VenueZone */
/** @typedef {'upcoming' | 'today' | 'started' | 'ended'} Phase */

/** How long after its start an event counts as 'started' (then 'ended'). */
export const STARTED_FOR_MS = 4 * 3600e3;
/** A start time read in a zone guessed from the region only counts once this has passed too. */
export const REGION_GRACE_MS = 3600e3;

// ---- zones -------------------------------------------------------------------------------

/** US states, DC and territories: the zone most of the state's people live in. */
const US = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: 'America/Chicago', CA: 'America/Los_Angeles',
  CO: 'America/Denver', CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York', FL: 'America/New_York',
  GA: 'America/New_York', HI: 'Pacific/Honolulu', ID: 'America/Boise', IL: 'America/Chicago', IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago', KS: 'America/Chicago', KY: 'America/New_York', LA: 'America/Chicago', ME: 'America/New_York',
  MD: 'America/New_York', MA: 'America/New_York', MI: 'America/Detroit', MN: 'America/Chicago', MS: 'America/Chicago',
  MO: 'America/Chicago', MT: 'America/Denver', NE: 'America/Chicago', NV: 'America/Los_Angeles', NH: 'America/New_York',
  NJ: 'America/New_York', NM: 'America/Denver', NY: 'America/New_York', NC: 'America/New_York', ND: 'America/Chicago',
  OH: 'America/New_York', OK: 'America/Chicago', OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago', UT: 'America/Denver',
  VT: 'America/New_York', VA: 'America/New_York', WA: 'America/Los_Angeles', WV: 'America/New_York', WI: 'America/Chicago',
  WY: 'America/Denver', PR: 'America/Puerto_Rico', GU: 'Pacific/Guam', VI: 'America/St_Thomas',
};

/** Canadian provinces and territories, by code and by name (HelloTickets writes names). */
const CA = {
  AB: 'America/Edmonton', BC: 'America/Vancouver', MB: 'America/Winnipeg', NB: 'America/Moncton', NL: 'America/St_Johns',
  NS: 'America/Halifax', NT: 'America/Yellowknife', NU: 'America/Iqaluit', ON: 'America/Toronto', PE: 'America/Halifax',
  QC: 'America/Toronto', SK: 'America/Regina', YT: 'America/Whitehorse',
  ALBERTA: 'America/Edmonton', 'BRITISH COLUMBIA': 'America/Vancouver', MANITOBA: 'America/Winnipeg',
  'NEW BRUNSWICK': 'America/Moncton', 'NEWFOUNDLAND AND LABRADOR': 'America/St_Johns', NEWFOUNDLAND: 'America/St_Johns',
  'NOVA SCOTIA': 'America/Halifax', 'NORTHWEST TERRITORIES': 'America/Yellowknife', NUNAVUT: 'America/Iqaluit',
  ONTARIO: 'America/Toronto', 'PRINCE EDWARD ISLAND': 'America/Halifax', QUEBEC: 'America/Toronto', 'QUÉBEC': 'America/Toronto',
  SASKATCHEWAN: 'America/Regina', YUKON: 'America/Whitehorse',
};

/** Australian states and territories. */
const AU = {
  NSW: 'Australia/Sydney', VIC: 'Australia/Melbourne', QLD: 'Australia/Brisbane', SA: 'Australia/Adelaide', WA: 'Australia/Perth',
  TAS: 'Australia/Hobart', ACT: 'Australia/Sydney', NT: 'Australia/Darwin',
  'NEW SOUTH WALES': 'Australia/Sydney', VICTORIA: 'Australia/Melbourne', QUEENSLAND: 'Australia/Brisbane',
  'SOUTH AUSTRALIA': 'Australia/Adelaide', 'WESTERN AUSTRALIA': 'Australia/Perth', TASMANIA: 'Australia/Hobart',
  'AUSTRALIAN CAPITAL TERRITORY': 'Australia/Sydney', 'NORTHERN TERRITORY': 'Australia/Darwin',
};

/** Countries on one zone (for nearly everyone who lives there). */
const SINGLE = {
  GB: 'Europe/London', IE: 'Europe/Dublin', FR: 'Europe/Paris', DE: 'Europe/Berlin', ES: 'Europe/Madrid', IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam', BE: 'Europe/Brussels', PT: 'Europe/Lisbon', AT: 'Europe/Vienna', CH: 'Europe/Zurich',
  LU: 'Europe/Luxembourg', MC: 'Europe/Monaco', DK: 'Europe/Copenhagen', SE: 'Europe/Stockholm', NO: 'Europe/Oslo',
  FI: 'Europe/Helsinki', IS: 'Atlantic/Reykjavik', PL: 'Europe/Warsaw', CZ: 'Europe/Prague', SK: 'Europe/Bratislava',
  HU: 'Europe/Budapest', SI: 'Europe/Ljubljana', HR: 'Europe/Zagreb', RS: 'Europe/Belgrade', RO: 'Europe/Bucharest',
  BG: 'Europe/Sofia', GR: 'Europe/Athens', CY: 'Asia/Nicosia', MT: 'Europe/Malta', EE: 'Europe/Tallinn', LV: 'Europe/Riga',
  LT: 'Europe/Vilnius', TR: 'Europe/Istanbul', IL: 'Asia/Jerusalem', AE: 'Asia/Dubai', JP: 'Asia/Tokyo', KR: 'Asia/Seoul',
  SG: 'Asia/Singapore', HK: 'Asia/Hong_Kong', TW: 'Asia/Taipei', PH: 'Asia/Manila', TH: 'Asia/Bangkok', IN: 'Asia/Kolkata',
  ZA: 'Africa/Johannesburg', PR: 'America/Puerto_Rico', CR: 'America/Costa_Rica', PA: 'America/Panama', CO: 'America/Bogota',
  PE: 'America/Lima', AR: 'America/Argentina/Buenos_Aires', UY: 'America/Montevideo', DO: 'America/Santo_Domingo',
  JM: 'America/Jamaica', BS: 'America/Nassau',
};

/** Country spellings partners use (scripts/country.mjs normalizes most of them already). */
const COUNTRY = { USA: 'US', 'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', CANADA: 'CA', UK: 'GB', 'UNITED KINGDOM': 'GB', AUSTRALIA: 'AU' };

/** @type {Map<string, Intl.DateTimeFormat | null>} */
const FORMATS = new Map();
/** A formatter for one IANA zone, or null when the runtime does not know the zone.
 * @param {string} zone */
const formatFor = (zone) => {
  if (!FORMATS.has(zone)) {
    let f = null;
    try {
      f = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      f = null;
    }
    FORMATS.set(zone, f);
  }
  return FORMATS.get(zone) ?? null;
};

/** Wall-clock parts of an instant in a zone.
 * @param {Intl.DateTimeFormat} f @param {number} ms */
const partsAt = (f, ms) => {
  /** @type {Record<string, number>} */
  const p = {};
  for (const { type, value } of f.formatToParts(new Date(ms))) if (type !== 'literal') p[type] = Number(value);
  return p;
};

/** "+02:00", "-0700", "Z" → minutes east of UTC; null when unreadable.
 * @param {string | null | undefined} s */
export const parseOffset = (s) => {
  if (typeof s !== 'string') return null;
  if (/^z$/i.test(s.trim())) return 0;
  const m = /^([+-])(\d{1,2}):?(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const mins = (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
  return Math.abs(mins) <= 14 * 60 ? mins : null;
};

/** The majority zone for a (country, region) pair, or null.
 * @param {string | null | undefined} country @param {string | null | undefined} region */
export const regionZone = (country, region) => {
  const raw = String(country ?? '').trim().toUpperCase();
  const cc = /** @type {Record<string, string>} */ (COUNTRY)[raw] ?? raw;
  const r = String(region ?? '').trim().toUpperCase();
  if (cc && cc in SINGLE) return /** @type {Record<string, string>} */ (SINGLE)[cc];
  // TicketNetwork rows whose country was not learned still name a US state
  if ((cc === 'US' || !cc) && r in US) return /** @type {Record<string, string>} */ (US)[r];
  if (cc === 'CA' && r in CA) return /** @type {Record<string, string>} */ (CA)[r];
  if (cc === 'AU' && r in AU) return /** @type {Record<string, string>} */ (AU)[r];
  return null;
};

/** Where the event's clock comes from (see the top of this file), or null.
 * @param {ShowEvent | null | undefined} e
 * @returns {VenueZone | null} */
export const venueZone = (e) => {
  const tz = e?.place?.tz;
  if (typeof tz === 'string' && tz && formatFor(tz)) return { kind: 'tz', zone: tz };
  const off = parseOffset(e?.place?.offset);
  if (off !== null) return { kind: 'offset', minutes: off };
  const guess = regionZone(e?.country, e?.place?.region);
  if (guess && formatFor(guess)) return { kind: 'region', zone: guess };
  return null;
};

/** The zone's UTC offset at an instant, in minutes east of UTC.
 * @param {VenueZone} vz @param {number} ms */
export const offsetAt = (vz, ms) => {
  if (vz.kind === 'offset') return vz.minutes;
  const f = formatFor(vz.zone);
  if (!f) return 0;
  const p = partsAt(f, ms);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 60000);
};

/** YYYY-MM-DD of an instant in the venue's zone.
 * @param {number} ms @param {VenueZone} vz */
export const venueDate = (ms, vz) => new Date(ms + offsetAt(vz, ms) * 60000).toISOString().slice(0, 10);

/** YYYY-MM-DD of an instant on the runtime's own clock (in a browser: the visitor's).
 * @param {number} ms */
export const localDate = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Whole calendar days from a to b (YYYY-MM-DD), DST-proof.
 * @param {string} a @param {string} b */
export const isoDays = (a, b) => Math.round((Date.parse(b + 'T12:00:00Z') - Date.parse(a + 'T12:00:00Z')) / 864e5);

/** "19:30" → [19, 30]; null for anything that is not a clock time.
 * @param {string | null | undefined} t */
const clock = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? [h, min] : null;
};

/** The instant a wall-clock date and time happen in a zone (DST-aware: two passes).
 * @param {string} date @param {string} time @param {VenueZone} vz */
export const wallToUtc = (date, time, vz) => {
  const c = clock(time);
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!c || !d) return null;
  const guess = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), c[0], c[1]);
  if (vz.kind === 'offset') return guess - vz.minutes * 60000;
  const first = offsetAt(vz, guess);
  let t = guess - first * 60000;
  const second = offsetAt(vz, t);
  if (second !== first) t = guess - second * 60000;
  return t;
};

/** When the event starts (ms), or null without a start time or a zone.
 * @param {ShowEvent | null | undefined} e */
export const startMs = (e) => {
  const vz = venueZone(e);
  return e && vz && e.time ? wallToUtc(e.date, e.time, vz) : null;
};

/** Calendar days from today to the event in the venue's zone (0 = today, 1 = tomorrow,
 *  negative = past). Without a zone, by the runtime's (visitor's) own date.
 * @param {ShowEvent} e @param {number} now */
export const daysOut = (e, now) => {
  const vz = venueZone(e);
  return isoDays(vz ? venueDate(now, vz) : localDate(now), e.date);
};

/**
 * Everything the page needs at once.
 *  - 'upcoming': 1 or more days left;
 *  - 'today': day 0, before the start (or with no start time);
 *  - 'started': from the start until start + 4 h (a zone guessed from the region only
 *    counts from start + 1 h). Never without a start time and a zone;
 *  - 'ended': from start + 4 h, or (no start time) once the date has passed.
 * @param {ShowEvent} e @param {number} now
 * @returns {{ phase: Phase, daysOut: number, start: number | null, zone: VenueZone | null }}
 */
export const showtime = (e, now) => {
  const zone = venueZone(e);
  const days = isoDays(zone ? venueDate(now, zone) : localDate(now), e.date);
  const start = zone && e.time ? wallToUtc(e.date, e.time, zone) : null;
  /** @type {Phase} */
  let phase;
  if (start !== null && zone && isLongEvent(e)) {
    // a long event is on until its local day ends
    if (days < 0) phase = 'ended';
    else if (now >= start + (zone.kind === 'region' ? REGION_GRACE_MS : 0)) phase = 'started';
    else phase = days >= 1 ? 'upcoming' : 'today';
  } else if (start !== null && zone) {
    if (now >= start + STARTED_FOR_MS) phase = 'ended';
    else if (now >= start + (zone.kind === 'region' ? REGION_GRACE_MS : 0)) phase = 'started';
    else phase = days >= 1 ? 'upcoming' : 'today';
  } else {
    phase = days >= 1 ? 'upcoming' : days === 0 ? 'today' : 'ended';
  }
  return { phase, daysOut: days, start, zone };
};

/** @param {ShowEvent} e @param {number} now */
export const phase = (e, now) => showtime(e, now).phase;

/** North American zones as a buyer reads them after a start time ("8:00 PM ET"), by the venue's EXACT
 *  IANA zone (place.tz: SeatGeek rows, the World Series desk's). A fixed table, never Intl output: browser
 *  engines word zone names differently and the page must hydrate to the same text (src/lib/clock.ts).
 *  Arizona keeps standard time all year (MST). */
/** @type {Record<string, string>} */
const ZONE_LABEL = {
  'America/New_York': 'ET', 'America/Detroit': 'ET', 'America/Indiana/Indianapolis': 'ET', 'America/Kentucky/Louisville': 'ET', 'America/Toronto': 'ET',
  'US/Eastern': 'ET', 'US/Michigan': 'ET', 'EST5EDT': 'ET',
  'America/Chicago': 'CT', 'America/Winnipeg': 'CT', 'US/Central': 'CT', 'CST6CDT': 'CT',
  'America/Denver': 'MT', 'America/Boise': 'MT', 'America/Edmonton': 'MT', 'US/Mountain': 'MT', 'MST7MDT': 'MT',
  'America/Phoenix': 'MST', 'US/Arizona': 'MST',
  'America/Los_Angeles': 'PT', 'America/Vancouver': 'PT', 'US/Pacific': 'PT', 'PST8PDT': 'PT',
  'America/Anchorage': 'AKT', 'US/Alaska': 'AKT',
  'Pacific/Honolulu': 'HT', 'US/Hawaii': 'HT',
};
/** The venue's zone for its start time ("ET"), or null: only from an exact place.tz the table knows —
 *  never from a fixed offset or a zone guessed from the region (East Tennessee is Eastern), which could
 *  name the wrong clock. The start time itself is always the venue's own.
 *  @param {{ place?: Place | null } | null | undefined} e */
export const zoneLabel = (e) => {
  const tz = e?.place?.tz;
  return typeof tz === 'string' && Object.prototype.hasOwnProperty.call(ZONE_LABEL, tz) ? ZONE_LABEL[tz] : null;
};

/** An evening start ("Tonight") rather than a daytime one ("Today"): 17:00 or later.
 * @param {string | null | undefined} time */
export const isEvening = (time) => {
  const c = clock(time);
  return !!c && c[0] >= 17;
};

// ---- start times a page may state ------------------------------------------------------------

/** Starts that really are early in the day: golf, racing, cricket, rugby, tennis, marathons… Everything else
 *  (a concert, a musical, a ball game, football) starting 00:00–09:59 local is a feed's stand-in, not a start. */
const EARLY_STARTS = /golf|tennis|cricket|rugby|racing|\bf1\b|formula|nascar|motogp|indycar|marathon|\brun(?:ning)?\b|cycling|triathlon|rowing|sailing|regatta|equestrian|polo|athletics|swimming|surf|ski|snowboard|fishing|breakfast|brunch|morning|sunrise|olympic|summer games|winter games|paralympic|\bla ?28\b|gymnastics|archery|biathlon|fencing|judo|rugby sevens|convention|\bexpo\b|festival|\bfest\b|attraction|museum|observatory|observation deck|\bzoo\b|aquarium|theme park|(?:stadium|studio|guided|walking|backstage|behind the scenes|city|bus) tours?\b|day pass|\bsessions?\b/i;

/** Events that run all day or several days: a festival or day pass, a multi-day pass, Test cricket, a tennis day session, a
 *  convention or expo, an attraction (museum, observation deck, zoo), a stadium or museum tour, an Olympic session. They stay
 *  listed until the venue's local day ends, never "over" 4 h after the listed start. A concert "Tour" is not one of them. */
const LONG_EVENT = /festival|\bfest\b|day pass|\b\d-day\b|multi-?day|weekend pass|season pass|test (?:match|cricket)|\bcricket\b|tennis|convention|\bexpo\b|comic[- ]?con|\battractions?\b|museum|observatory|observation deck|\bzoo\b|aquarium|theme park|(?:stadium|studio|guided|walking|backstage|behind the scenes|city|bus) tours?\b|olympic|summer games|winter games|paralympic|\bla ?28\b|gymnastics|archery|marathon|triathlon|golf|regatta|\bsessions?\b/i;
/** Does this event run past a normal performance's four hours?
 *  @param {{ category?: string | null, kind?: string | null, genre?: string | null, name?: string | null } | null | undefined} what */
export const isLongEvent = (what) => LONG_EVENT.test([what?.kind, what?.genre, what?.category, what?.name].filter(Boolean).join(' '));

/** Is this "start time" a stand-in rather than a start? 00:00–09:59 for a show that is not an early-day sport:
 *  SeatGeek parks unannounced MLB games at 03:30 / 03:33, the exchanges at 03:30, HelloTickets at 00:00.
 *  Such a time is shown as "time TBC", never as a start.
 *  @param {string | null | undefined} time
 *  @param {{ category?: string | null, kind?: string | null, genre?: string | null, name?: string | null } | null} [what] */
export const implausibleStart = (time, what) => {
  const c = clock(time);
  if (!c || c[0] >= 10) return false;
  const label = [what?.kind, what?.genre, what?.category, what?.name].filter(Boolean).join(' ');
  return !EARLY_STARTS.test(label);
};

/** `time` when it can be a start, else null.
 *  @param {string | null | undefined} time @param {Parameters<typeof implausibleStart>[1]} [what] */
export const plausibleTime = (time, what) => (clock(time) && !implausibleStart(time, what) ? /** @type {string} */ (time) : null);

/** "+02:00" for minutes east of UTC. @param {number} mins */
const offsetText = (mins) => `${mins < 0 ? '-' : '+'}${String(Math.floor(Math.abs(mins) / 60)).padStart(2, '0')}:${String(Math.abs(mins) % 60).padStart(2, '0')}`;

/** The zone a country keeps wholesale (France, Spain, Britain…), or null for the ones that span several. @param {string | null | undefined} country */
const wholeCountryZone = (country) => regionZone(country, '');

/** Does a stated UTC offset fit the venue's country? A country on one zone has one offset on a date: HelloTickets stores
 *  a 17:00 placeholder and some Spanish, Italian and British starts against New York's clock, so a Bordeaux kick-off came
 *  with "-05:00". Countries that span zones accept any offset.
 *  @param {{ date: string, time: string, country?: string | null }} e @param {number} minutes */
export const offsetFits = (e, minutes) => {
  const zone = wholeCountryZone(e.country);
  if (!zone || !formatFor(zone)) return true;
  /** @type {VenueZone} */
  const vz = { kind: 'tz', zone };
  const at = wallToUtc(e.date, e.time, vz);
  return at === null || Math.abs(offsetAt(vz, at) - minutes) <= 30;
};

/**
 * The Event schema's startDate: the date and time with the venue's own UTC offset ("2026-11-04T20:45:00+03:00"), the
 * date alone when the time or the zone is unknown (Google reads a date-only start as "time unknown"). The zone, best first:
 * the venue's IANA zone, a stated offset that fits the country, the zone of a country that keeps one.
 * @param {{ date: string, time?: string | null, country?: string | null, place?: Place | null }} e
 * @param {Parameters<typeof implausibleStart>[1]} [what]
 */
export const startIso = (e, what) => {
  if (!e?.date) return '';
  const time = plausibleTime(e.time, what);
  if (!time) return e.date;
  /** @type {VenueZone | null} */
  let vz = null;
  const tz = e.place?.tz;
  if (typeof tz === 'string' && tz && formatFor(tz)) vz = { kind: 'tz', zone: tz };
  const off = parseOffset(e.place?.offset);
  if (!vz && off !== null && offsetFits({ date: e.date, time, country: e.country }, off)) vz = { kind: 'offset', minutes: off };
  if (!vz) {
    const zone = wholeCountryZone(e.country);
    if (zone && formatFor(zone)) vz = { kind: 'tz', zone };
  }
  const at = vz ? wallToUtc(e.date, time, vz) : null;
  if (!vz || at === null) return e.date;
  return `${e.date}T${time}:00${offsetText(offsetAt(vz, at))}`;
};

/** startIso() of an event document, or '' without a time and a zone (the edge's meta tuple keeps '' for "unknown").
 *  @param {{ date: string, time?: string | null, country?: string | null, place?: Place | null, category?: string | null, kind?: string | null, genre?: string | null, name?: string | null }} e */
export const startIsoOnly = (e) => {
  const s = startIso(e, e);
  return s.includes('T') ? s : '';
};

// ---- is the show over? ----------------------------------------------------------------------

/** The offset of a North American venue from its longitude, DST ignored: -5 Eastern (lng > -82.5)… -8 Pacific.
 *  Standard time is the later clock in summer, so a show is never judged over an hour EARLY by it.
 *  @param {number | null | undefined} lng @returns {VenueZone | null} */
const longitudeZone = (lng) => {
  if (typeof lng !== 'number' || !Number.isFinite(lng) || lng === 0 || lng > -50 || lng < -170) return null;
  return { kind: 'offset', minutes: Math.max(-10, Math.min(-4, Math.floor(lng / 15))) * 60 };
};

/**
 * Has this show ended, judged at `now`, in the VENUE's clock?
 *  - a start time and a zone (the venue's, a stated offset, a country on one zone, or — for North America — the
 *    longitude): over from start + 4 hours (STARTED_FOR_MS), the same moment `showtime()` calls it 'ended';
 *  - otherwise by date only, the list pages' rule (shared/show-day.mjs): a date before the day its country is on,
 *    which for North America turns at 10:00 UTC and elsewhere at 00:00 UTC.
 * The date rule alone kept a 7 PM show of last night on every list until 6 AM Eastern.
 * @param {{ date: string, time?: string | null, country?: string | null, lng?: number | null, place?: Place | null, category?: string | null, kind?: string | null, genre?: string | null, name?: string | null }} e
 * @param {number} now
 */
export const showOver = (e, now) => {
  if (!e?.date) return false;
  const cc = String(e.country ?? '').toUpperCase();
  const northAmerica = !cc || cc === 'US' || cc === 'CA' || cc === 'MX' || cc === 'PR' || cc === 'USA' || cc === 'CANADA';
  const dateDay = new Date(now - (northAmerica ? 10 : 0) * 3600e3).toISOString().slice(0, 10);
  const byDate = e.date < dateDay;
  if (byDate) return true;
  // a festival, day pass, Test match, convention or attraction stays listed until its local day has ended
  if (isLongEvent(e)) {
    const lz = venueZone(e) ?? (northAmerica ? longitudeZone(e.lng) : null);
    return lz ? e.date < venueDate(now, lz) : false;
  }
  if (!e.time || !clock(e.time)) return false;
  const vz = venueZone(e) ?? (northAmerica ? longitudeZone(e.lng) : null);
  const start = vz ? wallToUtc(e.date, e.time, vz) : null;
  if (start === null) return false;
  return now >= start + STARTED_FOR_MS + (vz && vz.kind === 'region' ? REGION_GRACE_MS : 0);
};
